// frontend/src/hooks/useConnectors.js
//
// Read hook for the Connectors settings page — lists ConnectorInstance
// rows for the currently selected store. Read-only; no mutations.
//
// The backend endpoint excludes _encrypted_config (credentials) and
// sync_state (adapter-specific shape) from the wire — the page renders
// role, vendor, status, configured_at, last-sync timestamps, and whether an
// activation / deactivation is in flight.
//
// Not only the settings page reads this: through `useActiveErp`, Link-to-ERP
// dialogs take the connector id from it and banners announce the ERP from it.
// So an unreadable payload has to become an ERROR here rather than an empty
// list — see utils/connectorRows.js for the shape.

import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import { useAuth } from '../contexts/AuthContext';
import { apiJson, readResult } from '../queries/api';
import { connectors as connectorsKey } from '../queries/keys';
import { activeErpConnector, connectorRowsFrom } from '../utils/connectorRows';

export function useConnectors() {
  const { t } = useTranslation();
  const { selectedStore } = useAuth();
  const storeId = selectedStore?.id;

  const query = useQuery({
    queryKey: connectorsKey.all(storeId),
    queryFn: async () => {
      const rows = connectorRowsFrom(await apiJson.get('/api/connectors/'));
      // Thrown, not returned as `[]`: in a queryFn a throw is how TanStack is
      // told the read failed, and "we could not read the connector list" must
      // not reach a screen looking like "this store has no connectors".
      if (rows === null) throw new Error('/api/connectors/ did not return a connector page');
      return rows;
    },
    enabled: !!storeId,
    staleTime: 30_000,
  });

  return readResult(query, t, 'connectors.loadFailed');
}

/**
 * The ERP connector that owns this store's PO lifecycle, for the screens that
 * name or link it. `connector` is null when the store has none, and also
 * while the list is loading or failed — `error` then says the list failed.
 */
export function useActiveErp() {
  const { data, error } = useConnectors();
  return { connector: data ? activeErpConnector(data) : null, error };
}
